import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const TOKEN_KEY = "tabel_session_token";

export const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
};
export const setStoredToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* noop */ }
};

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-Tabel-Client": "web",
  },
});

// Attach Authorization header from localStorage on every request — works on mobile Safari/Chrome
// where 3rd-party cookies are blocked. Cookies still sent as fallback for desktop.
api.interceptors.request.use((config) => {
  const tok = getStoredToken();
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

// On 401 — clear stored token (forces re-login)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      setStoredToken(null);
    }
    return Promise.reject(err);
  }
);

export default api;

// === Auth ===
const _saveToken = (resp) => {
  if (resp?.data?.token) setStoredToken(resp.data.token);
  return resp;
};
export const exchangeSession = (session_id) =>
  api.post("/auth/session", { session_id }).then(_saveToken);
export const fetchMe = () => api.get("/auth/me");
export const logoutApi = () =>
  api.post("/auth/logout").finally(() => setStoredToken(null));
export const registerApi = (data) =>
  api.post("/auth/register", data).then(_saveToken);
export const loginApi = (data) =>
  api.post("/auth/login", data).then(_saveToken);
export const forgotPasswordApi = (email) => api.post("/auth/forgot-password", { email }).then(r => r.data);
export const resetPasswordApi = (code, password) => api.post("/auth/reset-password", { code, password });
export const changePasswordApi = (old_password, new_password) =>
  api.post("/auth/change-password", { old_password, new_password });
export const changeEmailApi = (new_email, current_password) =>
  api.post("/auth/change-email", { new_email, current_password });

// === ЭЦП НУЦ РК ===
export const ecpChallengeApi = () => api.post("/auth/ecp/challenge").then(r => r.data);
export const ecpVerifyApi = (challenge_id, cms_b64) =>
  api.post("/auth/ecp/verify", { challenge_id, cms_b64, mode: "login" }).then(_saveToken).then(r => r.data);
export const ecpBindApi = (challenge_id, cms_b64) =>
  api.post("/auth/ecp/bind", { challenge_id, cms_b64 }).then(r => r.data);
export const ecpRegisterCompanyApi = (challenge_id, cms_b64, week_type, salary) =>
  api.post("/auth/ecp/register-company", { challenge_id, cms_b64, week_type, salary }).then(r => r.data);

// === Документы ===
export const listDocumentsApi = (params = {}) =>
  api.get("/documents", { params }).then(r => r.data);
export const documentCategoriesApi = () =>
  api.get("/documents/categories").then(r => r.data);
export const updateDocumentCategoriesApi = (categories) =>
  api.put("/documents/categories", { categories }).then(r => r.data);
export const uploadDocumentApi = (file, { title, category, user_id, visible_to_employee = true } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("title", title);
  fd.append("category", category);
  if (user_id) fd.append("user_id", user_id);
  fd.append("visible_to_employee", String(visible_to_employee));
  return api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const updateDocumentApi = (doc_id, payload) =>
  api.put(`/documents/${doc_id}`, payload).then(r => r.data);
export const deleteDocumentApi = (doc_id) =>
  api.delete(`/documents/${doc_id}`).then(r => r.data);
export const documentDownloadUrlApi = (doc_id) =>
  api.get(`/documents/${doc_id}/download`).then(r => r.data);

// === Company ===
export const createCompany = (data) => api.post("/companies", data);
export const updateTaxes = (tax_config) => api.put("/companies/me/taxes", { tax_config });
export const updateSocialStatuses = (social_statuses) =>
  api.put("/companies/me/social-statuses", { social_statuses }).then(r => r.data);
export const updateWithholdingKinds = (withholding_kind_defs) =>
  api.put("/companies/me/withholding-kinds", { withholding_kind_defs }).then(r => r.data);
export const updateCompanyApi = (data) => api.put("/companies/me", data).then(r => r.data);

// === Users ===
export const listCompanyUsers = () => api.get("/users/company").then(r => r.data);
export const listAllUsers = () => api.get("/users/all").then(r => r.data);
export const exportDirectory = async (format = "xlsx") => {
  const r = await api.get("/directory-export", {
    params: { format },
    responseType: "blob",
  });
  const isPdf = format === "pdf";
  const blob = new Blob([r.data], {
    type: isPdf ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `directory.${isPdf ? "pdf" : "xlsx"}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
export const getUser = (uid) => api.get(`/users/${uid}`).then(r => r.data);
export const createEmployee = (data) => api.post("/users", data).then(r => r.data);
export const updateUser = (uid, data) => api.put(`/users/${uid}`, data);

// === Tasks ===
export const listTasks = (params = {}) => api.get("/tasks", { params }).then(r => r.data);
export const getTask = (id) => api.get(`/tasks/${id}`).then(r => r.data);
export const createTask = (data) => api.post("/tasks", data).then(r => r.data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data).then(r => r.data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`).then(r => r.data);
export const markTaskDone = (id) => api.post(`/tasks/${id}/done`).then(r => r.data);
export const reopenTask = (id) => api.post(`/tasks/${id}/reopen`).then(r => r.data);
export const tasksBadge = () => api.get("/tasks/badge").then(r => r.data);
export const listTaskComments = (id) => api.get(`/tasks/${id}/comments`).then(r => r.data);
export const addTaskComment = (id, text) => api.post(`/tasks/${id}/comments`, { text }).then(r => r.data);

// === Bulk import (Excel) ===
export const downloadEmployeeTemplate = () =>
  api.get("/employees/import-template", { responseType: "blob" }).then(r => {
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "tabel_employees_template.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  });
export const importEmployees = (file, mode = "validate") => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/employees/import?mode=${mode}`, fd,
    { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};

// === Calendar ===
export const getCalendar = (year, month) => api.get(`/calendar/${year}/${month}`).then(r => r.data);
export const saveCalendar = (year, month, days) => api.put("/calendar", { year, month, days });

// === Timesheet ===
export const getTimesheet = (year, month) => api.get(`/timesheet/${year}/${month}`).then(r => r.data);
export const listFiredForPeriod = (year, month) =>
  api.get(`/users/fired-for-period/${year}/${month}`).then(r => r.data);
export const setTimesheetEntry = (year, month, user_id, day, status) =>
  api.post("/timesheet", { year, month, user_id, day, status });
export const exportTimesheet = async (year, month, format, locationId = "all") => {
  const params = { format };
  if (locationId && locationId !== "all") params.location_id = locationId;
  const r = await api.get(`/timesheet/${year}/${month}/export`, {
    params,
    responseType: "blob",
  });
  const blob = new Blob([r.data], {
    type: format === "pdf"
      ? "application/pdf"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tabel_${year}_${String(month + 1).padStart(2, "0")}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// === Chat ===
export const listMessages = (other_uid, since) =>
  api.get(`/chat/${other_uid}/messages`, { params: since ? { since } : {} }).then(r => r.data);
export const sendMessage = (to_user_id, text) =>
  api.post("/chat/send", { to_user_id, text }).then(r => r.data);

// === Invitations ===
export const sendInvitationApi = (data) => api.post("/invitations", data).then(r => r.data);
export const myInvitationsApi = () => api.get("/invitations/mine").then(r => r.data);
export const sentInvitationsApi = () => api.get("/invitations/sent").then(r => r.data);
export const acceptInvitationApi = (id) => api.post(`/invitations/${id}/accept`).then(r => r.data);
export const declineInvitationApi = (id) => api.post(`/invitations/${id}/decline`).then(r => r.data);
export const cancelInvitationApi = (id) => api.post(`/invitations/${id}/cancel`).then(r => r.data);

// === Employment history (Этап 3) ===
export const listEmploymentHistory = (status_filter = "all") =>
  api.get("/employment-history", { params: { status_filter } }).then(r => r.data);
export const fireEmployeeApi = (uid, reason) =>
  api.post(`/employees/${uid}/fire`, { reason: reason || "" }).then(r => r.data);
export const rehireEmployeeApi = (uid, data) =>
  api.post(`/employees/${uid}/rehire`, data).then(r => r.data);
export const exportEmploymentHistory = async () => {
  const r = await api.get("/employment-history/export", { responseType: "blob" });
  const blob = new Blob([r.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `history_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// === Feedback (admin contact) ===
export const submitFeedbackApi = (data) => api.post("/feedback", data).then(r => r.data);
export const listFeedbackApi = (status_filter = "all") =>
  api.get("/feedback", { params: { status_filter } }).then(r => r.data);
export const setFeedbackStatusApi = (fid, new_status) =>
  api.put(`/feedback/${fid}/status`, null, { params: { new_status } }).then(r => r.data);

// === Site admin ===
export const adminStatsApi = (days = 30) =>
  api.get("/admin/stats", { params: { days } }).then(r => r.data);
export const adminCompaniesApi = (params = {}) =>
  api.get("/admin/companies", { params }).then(r => r.data);
export const adminUsersApi = (params = {}) =>
  api.get("/admin/users", { params }).then(r => r.data);
export const adminResetUserLoginsApi = (userId) =>
  api.post(`/admin/users/${userId}/reset-logins`).then(r => r.data);
export const adminEventsApi = (params = {}) =>
  api.get("/admin/events", { params }).then(r => r.data);

// === Landing demo export (public) ===
export const exportDemoTimesheet = async (payload, format = "xlsx") => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const r = await axios.post(
    `${backendUrl}/api/demo/timesheet-export`,
    payload,
    { params: { format }, responseType: "blob" },
  );
  const blob = new Blob([r.data], {
    type: format === "pdf"
      ? "application/pdf"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `demo_timesheet_${payload.year}_${String(payload.month + 1).padStart(2, "0")}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// === Locations / Branches ===
export const listLocations = () => api.get("/locations").then(r => r.data);
export const createLocationApi = (data) => api.post("/locations", data).then(r => r.data);
export const updateLocationApi = (id, data) => api.put(`/locations/${id}`, data).then(r => r.data);
export const deleteLocationApi = (id) => api.delete(`/locations/${id}`).then(r => r.data);

// === Payroll export ===
export const exportPayroll = async (year, month, format, { weekly = false } = {}) => {
  const r = await api.get(`/payroll/${year}/${month}/export`, {
    params: { format, weekly: weekly ? 1 : 0 }, responseType: "blob",
  });
  const blob = new Blob([r.data], {
    type: format === "pdf"
      ? "application/pdf"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payroll_${year}_${String(month + 1).padStart(2, "0")}${weekly ? "_weekly" : ""}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// === Bonuses & Allowances ===
export const listBonuses = (params = {}) => api.get("/bonuses", { params }).then(r => r.data);
export const createBonusApi = (data) => api.post("/bonuses", data).then(r => r.data);
export const updateBonusApi = (id, data) => api.put(`/bonuses/${id}`, data).then(r => r.data);
export const deleteBonusApi = (id) => api.delete(`/bonuses/${id}`).then(r => r.data);

export const exportBonusesRegister = async (year, month) => {
  const r = await api.get("/bonuses-export", {
    params: { year, month },
    responseType: "blob",
  });
  const blob = new Blob([r.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bonuses_${year}_${String(month + 1).padStart(2, "0")}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const listAllowances = (params = {}) => api.get("/allowances", { params }).then(r => r.data);
export const createAllowanceApi = (data) => api.post("/allowances", data).then(r => r.data);
export const updateAllowanceApi = (id, data) => api.put(`/allowances/${id}`, data).then(r => r.data);
export const deleteAllowanceApi = (id) => api.delete(`/allowances/${id}`).then(r => r.data);

// === Withholdings ===
export const listWithholdings = (params = {}) => api.get("/withholdings", { params }).then(r => r.data);
export const createWithholdingApi = (data) => api.post("/withholdings", data).then(r => r.data);
export const updateWithholdingApi = (id, data) => api.put(`/withholdings/${id}`, data).then(r => r.data);
export const deleteWithholdingApi = (id) => api.delete(`/withholdings/${id}`).then(r => r.data);

export const listRecurringWithholdings = (params = {}) =>
  api.get("/recurring-withholdings", { params }).then(r => r.data);
export const createRecurringWithholdingApi = (data) =>
  api.post("/recurring-withholdings", data).then(r => r.data);
export const updateRecurringWithholdingApi = (id, data) =>
  api.put(`/recurring-withholdings/${id}`, data).then(r => r.data);
export const deleteRecurringWithholdingApi = (id) =>
  api.delete(`/recurring-withholdings/${id}`).then(r => r.data);

export const cancelWithholdingApi = (id, comment) =>
  api.post(`/withholdings/${id}/cancel`, { comment }).then(r => r.data);
export const getWithholdingHistory = (id) =>
  api.get(`/withholdings/${id}/history`).then(r => r.data);
export const stopRecurringWithholdingApi = (id, comment) =>
  api.post(`/recurring-withholdings/${id}/stop`, { comment }).then(r => r.data);
export const getRecurringWithholdingHistory = (id) =>
  api.get(`/recurring-withholdings/${id}/history`).then(r => r.data);

export const exportWithholdingsRegister = async (year, month) => {
  const r = await api.get("/withholdings-export", {
    params: { year, month },
    responseType: "blob",
  });
  const blob = new Blob([r.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `withholdings_${year}_${String(month + 1).padStart(2, "0")}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// === Vacation schedule ===
export const listVacationPlans = (params = {}) =>
  api.get("/vacation-plans", { params }).then(r => r.data);
export const getVacationBalance = (year, userId) =>
  api.get("/vacation-plans/balance", { params: { year, user_id: userId } }).then(r => r.data);
export const createVacationPlanApi = (data) =>
  api.post("/vacation-plans", data).then(r => r.data);
export const updateVacationPlanApi = (id, data) =>
  api.put(`/vacation-plans/${id}`, data).then(r => r.data);
export const submitVacationPlanApi = (id) =>
  api.post(`/vacation-plans/${id}/submit`).then(r => r.data);
export const approveVacationPlanApi = (id) =>
  api.post(`/vacation-plans/${id}/approve`).then(r => r.data);
export const rejectVacationPlanApi = (id, data) =>
  api.post(`/vacation-plans/${id}/reject`, data).then(r => r.data);
export const cancelVacationPlanApi = (id) =>
  api.post(`/vacation-plans/${id}/cancel`).then(r => r.data);
export const deleteVacationPlanApi = (id) =>
  api.delete(`/vacation-plans/${id}`).then(r => r.data);

export const exportVacationPlans = async (year, format = "xlsx") => {
  // Use dash variant to avoid route ambiguity with `/vacation-plans/{plan_id}`.
  const r = await api.get("/vacation-plans-export", {
    params: { year, format },
    responseType: "blob",
  });
  const isPdf = format === "pdf";
  const blob = new Blob([r.data], {
    type: isPdf ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vacation_schedule_${year}.${isPdf ? "pdf" : "xlsx"}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// === User consents (terms / privacy click-wrap) ===
export const getConsentMe = () => api.get("/consents/me").then(r => r.data);
export const acceptConsentApi = (method = "banner") =>
  api.post("/consents/accept", { method }).then(r => r.data);
